<template>
  <div :data-testid="dataTestid" class="h-44 w-full">
    <VChart
      :data-testid="`${dataTestid}-chart`"
      :option="option"
      :theme="OMR_ECHARTS_THEME"
      autoresize
      class="h-full w-full"
      @update-axis-pointer="onAxisPointer"
      @globalout="emit('hover', null)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { OMR_ECHARTS_THEME } from '@/composables/useOmrEchartsTheme'
import { VChart, type OmrEChartsOption } from '@/config/echarts'
import { makeNumber } from '@/helpers/chartFormat'

const props = withDefaults(
  defineProps<{
    categories: string[]
    values: number[]
    color: string
    locale: string
    dataTestid?: string
  }>(),
  { dataTestid: 'insights-rank-line-chart' },
)

const emit = defineEmits<{ hover: [index: number | null] }>()

interface AxisPointerInfo {
  axisDim: string
  value: number
}

const onAxisPointer = (event: { [key: string]: unknown }) => {
  const axesInfo = (event.axesInfo ?? []) as AxisPointerInfo[]
  const xAxis = axesInfo.find((info) => info.axisDim === 'x')
  emit('hover', xAxis ? xAxis.value : null)
}

const formatRank = computed(() => {
  const formatNumber = makeNumber(props.locale)
  return (value: number): string => `#${formatNumber(value)}`
})

const option = computed<OmrEChartsOption>(() => {
  const best = Math.min(...props.values)
  const worst = Math.max(...props.values)
  const min = Math.max(1, best - 1)
  const max = worst + 1
  const lastIndex = props.values.length - 1

  return {
    tooltip: { trigger: 'axis', showContent: false },
    grid: { top: 24, left: 8, right: 16, bottom: 8, containLabel: true },
    xAxis: {
      type: 'category',
      data: props.categories,
      boundaryGap: false,
    },
    yAxis: {
      type: 'value',
      inverse: true,
      min,
      max,
      interval: 1,
      splitLine: { show: false },
      axisLabel: {
        formatter: (value: number | string) => {
          const rank = Number(value)
          return rank === best || rank === worst ? formatRank.value(rank) : ''
        },
      },
    },
    series: [
      {
        type: 'line',
        color: props.color,
        symbolSize: 7,
        areaStyle: { opacity: 0.1, origin: 'end' },
        label: { show: false },
        data: props.values.map((value, index) => ({ value, symbol: index === lastIndex ? 'circle' : 'none' })),
      },
    ],
  }
})
</script>
